import {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {useTranslation} from "react-i18next";
import {useSnackbarEx} from "@/lib/snackbarex";
import {CommonI18N} from "@/translations";
import {
    clampPage,
    OverviewSearchResult,
    OverviewState,
    OverviewStateAdapter,
    resetToDefaults,
    SortDirection
} from "@/common/overview-state/overview-state-model";

/**
 * Debounce delay shared by `setSearchText()` and `setFilters()` (Standing constraint 4 of the
 * "Spaltenkopf-Filterung" plan): a free-text search and a column-header filter change race against the
 * same 250 ms window, and both go through the SAME timer (`searchDebounceRef` below), never two
 * independent ones — two independent timers would let a stale, debounced search query race a
 * sort/page change that queries immediately, see the module doc for the concrete failure this once
 * caused. Every other setter (`setSort`, `setSize`, `setPage`, `resetAll`) queries immediately.
 */
const DEBOUNCE_MS = 250;

/** Return shape of `useOverviewTableState` — the full behavior contract #1607 wires up to controls. */
export interface UseOverviewTableStateResult<TFilter, TRow, TSortField extends string = string> {
    /** The state that actually drives the current search — everything else derives from this. */
    readonly applied: OverviewState<TFilter, TSortField>;
    /** Rows of the most recently accepted (non-superseded) search response. */
    readonly rows: TRow[];
    readonly totalElements: number;
    readonly totalPages: number;
    /** Sum of unread messages over the whole filtered set, not the page — drives the Info column header badge. */
    readonly totalUnreadMessages: number;
    /** "Everything selected" for this variant (`adapter.defaults()`) — the badge's comparison baseline. */
    readonly defaults: TFilter;
    /** The variant's page-size default (`adapter.defaultSize()`) — what a Reset returns `size` to. */
    readonly defaultSize: number;
    /** Applies the adapter's defaults, clears the persisted document, and queries once. */
    resetAll(): void;
    /** Updates the free-text search immediately and queries after a 250 ms debounce. */
    setSearchText(text: string): void;
    /**
     * Volatile sort (Phase 0, Entscheidung 1) — never persisted, queries immediately, and always jumps to
     * page 0 (a sort change can move the row the user was looking at off the current page). Calling this
     * with `setSort(undefined, undefined)` — what the "Sortierung aufheben" menu entry
     * (`sortMenuChoice`) produces — IS the neutral state: it clears both fields and restores the fachliche
     * Standardreihenfolge.
     */
    setSort(field: TSortField | undefined, direction: SortDirection | undefined): void;
    /** Persists the new page size immediately and queries once. */
    setSize(size: number): void;
    /** Jumps to a page without persisting it, and queries once. */
    setPage(page: number): void;
    /**
     * Merges a partial change directly into `applied.filters`, jumps to page 0, and queries and persists
     * after the shared 250 ms debounce (Standing constraint 4 — filtering now takes effect immediately,
     * there is no draft state left to hold the change in the meantime).
     */
    setFilters(partial: Partial<TFilter>): void;
}

/** Narrows an opaque persisted document down to its numeric `size` field, if it has one. */
function extractDocumentSize(doc: unknown, fallback: number): number {
    if (typeof doc === "object" && doc !== null && "size" in doc) {
        const value = (doc as { size?: unknown }).size;
        if (typeof value === "number") {
            return value;
        }
    }
    return fallback;
}

function buildInitialState<TFilter, TSortField extends string>(
    filters: TFilter,
    size: number
): OverviewState<TFilter, TSortField> {
    return {
        page: 0,
        size,
        searchText: "",
        sortField: undefined,
        sortDirection: undefined,
        filters
    };
}

/**
 * Generic behavior behind every overview table variant (#1606). Knows only the fields declared on
 * `OverviewState` directly (`page`, `size`, `searchText`, `sortField`, `sortDirection`) plus the opaque
 * `filters` object; everything variant-specific comes in through the `adapter` argument
 * (`OverviewStateAdapter`, see `overview-state-model.ts`) — this hook contains no knowledge of any
 * concrete filter shape, document shape, or endpoint.
 *
 * On mount, `adapter.loadDocument()` runs once: a stored document seeds `applied` (missing status-like
 * fields fall back to `adapter.defaults()`, handled by `adapter.fromDocument`); the 204-empty-body case
 * (`undefined`) keeps the built-in default state. Either way, the very first search only fires after that
 * load settles — success or failure — never before and never twice.
 *
 * `resetAll`, `setSort`, `setSize` and `setPage` each trigger exactly one search immediately.
 * `setSearchText` and `setFilters` share ONE debounce timer (`searchDebounceRef`, Standing constraint 4):
 * either one reschedules it, so two changes within the same 250 ms window — of either kind, or one of
 * each — still produce exactly one query. The timer's own run (`runDebouncedAction`) always reads the
 * state to query from `appliedRef.current` at the moment it actually fires, never from a value captured
 * when the timer was scheduled — a captured snapshot would let a stale, filter-less query overtake an
 * immediately-queried sort change that ran in between and win the monotonic counter, restoring the header
 * to look sorted while the rows silently are not.
 *
 * Every immediately-querying mutator (`setSort`, `setSize`, `setPage`, `resetAll`) clears the pending
 * debounce timer FIRST, before doing anything else — otherwise the debounced run described above could
 * still fire later and undo what the immediate mutator just did. A separate ref,
 * `pendingFilterPersistRef` (never the timer itself), marks whether a `setFilters` call is still waiting
 * to be persisted: `setFilters` sets it, `setSearchText` never touches it (a search-only debounce run
 * persists nothing, `searchText` is not even a field of the persisted document, see
 * `requestStateToDocument`). Whoever clears the timer ABANDONS it but PROCESSES the flag instead of
 * dropping it — `setSort` and `setPage` persist the document built from `appliedRef.current` when the flag
 * is set, then clear it (`flushPendingFilterPersist`); `setSize` always persists anyway with the
 * just-updated state, which already covers the pending filter, so it simply clears the flag without a
 * second save; `resetAll` clears the flag WITHOUT persisting, since it is about to replace the document
 * with `clearDocument()` instead. The unmount cleanup persists once more if the flag is still set — that
 * `persist()` call is fire-and-forget and depends on no React state, so it survives the unmount fine.
 *
 * Persistence (`saveDocument` / `clearDocument`) always runs independently of the search — neither ever
 * waits for the other, and a failed persistence call shows an error without rolling back the
 * already-applied local state or skipping the query. Search responses are guarded by a monotonic counter:
 * only the response to the latest search writes rows/page data, and a superseded response — success or
 * failure — is silently dropped. After every accepted response, `clampPage` may trigger exactly one
 * follow-up reload with the corrected page; that reload never triggers a further reload of its own.
 */
export function useOverviewTableState<TDoc, TRow, TFilter, TSearchParams, TSortField extends string = string>(
    adapter: OverviewStateAdapter<TDoc, TRow, TFilter, TSearchParams, TSortField>
): UseOverviewTableStateResult<TFilter, TRow, TSortField> {
    const {t} = useTranslation();
    const {showMessage} = useSnackbarEx();

    const [applied, setAppliedState] = useState<OverviewState<TFilter, TSortField>>(() =>
        buildInitialState(adapter.defaults(), adapter.defaultSize())
    );
    const [rows, setRows] = useState<TRow[]>([]);
    const [totalElements, setTotalElements] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [totalUnreadMessages, setTotalUnreadMessages] = useState(0);

    const queryCounterRef = useRef(0);
    const searchDebounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);
    /** Set by `setFilters`, read and cleared by whichever mutator processes it next — see the module doc. */
    const pendingFilterPersistRef = useRef(false);

    // Mirrors `applied`, updated by every write below. A useCallback dependency array is only
    // re-evaluated between renders — two mutators invoked back to back within the same synchronous
    // handler (before React re-renders) would otherwise both read the same pre-handler snapshot from
    // their memoized closures, and the second call would silently discard the first change. Reading
    // through this ref instead makes every mutator see the other's just-applied state and lets the
    // mutators drop `applied` from their dependency arrays entirely.
    const appliedRef = useRef(applied);

    const setApplied = useCallback((next: OverviewState<TFilter, TSortField>) => {
        appliedRef.current = next;
        setAppliedState(next);
    }, []);

    // Refs so the adapter and the error handler can be read from the stable callbacks below without
    // forcing those callbacks to change identity whenever the caller re-creates the adapter object.
    const adapterRef = useRef(adapter);
    adapterRef.current = adapter;
    const reportErrorRef = useRef((error: unknown) => {
        showMessage({summary: t(CommonI18N.message.unknownError), severity: "error", error});
    });
    reportErrorRef.current = (error: unknown) => {
        showMessage({summary: t(CommonI18N.message.unknownError), severity: "error", error});
    };

    // Fires a persistence call (saveDocument/clearDocument) without ever letting it block or roll back
    // the query it accompanies — a rejection is reported but otherwise swallowed, per "Standing
    // constraints" ("ein fehlgeschlagenes Speichern darf die Abfrage nie verhindern und nie zurücknehmen").
    const persist = useCallback(async (action: () => Promise<void>) => {
        try {
            await action();
        } catch (error: unknown) {
            reportErrorRef.current(error);
        }
    }, []);

    // Read through a ref (not called by name) so the recursive clamp-reload call below does not turn
    // "runSearch" itself into a dependency — the callback stays stable forever, adapter and the error
    // reporter already being read through the refs above.
    const runSearchRef = useRef<(state: OverviewState<TFilter, TSortField>, isClampReload?: boolean) => void>(
        undefined
    );

    const runSearch = useCallback(async (state: OverviewState<TFilter, TSortField>, isClampReload = false) => {
        queryCounterRef.current += 1;
        const queryId = queryCounterRef.current;
        try {
            const result: OverviewSearchResult<TRow> = await adapterRef.current.search(
                adapterRef.current.toSearchParams(state)
            );
            if (queryCounterRef.current !== queryId) {
                // Superseded by a newer query — silently dropped, per "Standing constraints".
                return;
            }
            setRows(result.rows);
            setTotalElements(result.totalElements);
            setTotalPages(result.totalPages);
            setTotalUnreadMessages(result.totalUnreadMessages);
            const clamped = clampPage(result.page, result.totalPages);
            if (!isClampReload && clamped !== result.page) {
                const reloadState = {...state, page: clamped};
                setApplied(reloadState);
                runSearchRef.current?.(reloadState, true);
            }
        } catch (error: unknown) {
            if (queryCounterRef.current !== queryId) {
                // A superseded query never surfaces an error message either.
                return;
            }
            reportErrorRef.current(error);
        }
    }, [setApplied]);
    runSearchRef.current = runSearch;

    useEffect(() => {
        let cancelled = false;

        const loadAndSearch = async () => {
            const initialState = buildInitialState<TFilter, TSortField>(
                adapterRef.current.defaults(),
                adapterRef.current.defaultSize()
            );
            let stateForFirstSearch = initialState;
            try {
                const doc = await adapterRef.current.loadDocument();
                if (cancelled) {
                    return;
                }
                const defaults = adapterRef.current.defaults();
                const filters = adapterRef.current.fromDocument(doc, defaults);
                const size = extractDocumentSize(doc, initialState.size);
                const loaded: OverviewState<TFilter, TSortField> = {...initialState, filters, size};
                setApplied(loaded);
                stateForFirstSearch = loaded;
            } catch (error: unknown) {
                if (!cancelled) {
                    reportErrorRef.current(error);
                }
            }
            if (!cancelled) {
                runSearch(stateForFirstSearch);
            }
        };

        loadAndSearch();

        return () => {
            cancelled = true;
        };
    }, [runSearch, setApplied]);

    // Cancels the shared debounce timer WITHOUT touching `pendingFilterPersistRef` — callers that still
    // need to process a pending flag call `flushPendingFilterPersist` instead, never this directly
    // followed by their own ad-hoc flag handling.
    const clearDebounceTimer = useCallback(() => {
        if (searchDebounceRef.current) {
            clearTimeout(searchDebounceRef.current);
            searchDebounceRef.current = undefined;
        }
    }, []);

    // The shared debounce timer's own run: always reads `appliedRef.current` at fire time (see the module
    // doc), queries once, and persists once more if `setFilters` left the flag standing.
    const runDebouncedAction = useCallback(() => {
        searchDebounceRef.current = undefined;
        const current = appliedRef.current;
        runSearch(current);
        if (pendingFilterPersistRef.current) {
            pendingFilterPersistRef.current = false;
            persist(() => adapterRef.current.saveDocument(adapterRef.current.toDocument(current)));
        }
    }, [persist, runSearch]);

    const scheduleDebouncedAction = useCallback(() => {
        clearDebounceTimer();
        searchDebounceRef.current = setTimeout(runDebouncedAction, DEBOUNCE_MS);
    }, [clearDebounceTimer, runDebouncedAction]);

    // Used by every mutator that queries immediately and therefore must abandon the shared debounce
    // timer (Standing constraint 4): cancels the timer and, if `setFilters` left a persist pending,
    // performs it right now against `appliedRef.current` — the state as it stands BEFORE the calling
    // mutator applies its own change — instead of silently dropping it.
    const flushPendingFilterPersist = useCallback(() => {
        clearDebounceTimer();
        if (pendingFilterPersistRef.current) {
            pendingFilterPersistRef.current = false;
            persist(() => adapterRef.current.saveDocument(adapterRef.current.toDocument(appliedRef.current)));
        }
    }, [clearDebounceTimer, persist]);

    const resetAll = useCallback(() => {
        clearDebounceTimer();
        pendingFilterPersistRef.current = false;
        const next = resetToDefaults(
            appliedRef.current,
            adapterRef.current.defaults(),
            adapterRef.current.defaultSize()
        );
        setApplied(next);
        persist(() => adapterRef.current.clearDocument());
        runSearch(next);
    }, [clearDebounceTimer, persist, runSearch, setApplied]);

    const setSearchText = useCallback(
        (text: string) => {
            const next = {...appliedRef.current, searchText: text, page: 0};
            setApplied(next);
            scheduleDebouncedAction();
        },
        [scheduleDebouncedAction, setApplied]
    );

    const setSort = useCallback(
        (field: TSortField | undefined, direction: SortDirection | undefined) => {
            flushPendingFilterPersist();
            const next = {...appliedRef.current, sortField: field, sortDirection: direction, page: 0};
            setApplied(next);
            runSearch(next);
        },
        [flushPendingFilterPersist, runSearch, setApplied]
    );

    const setSize = useCallback(
        (size: number) => {
            clearDebounceTimer();
            pendingFilterPersistRef.current = false;
            const next = {...appliedRef.current, size, page: 0};
            setApplied(next);
            persist(() => adapterRef.current.saveDocument(adapterRef.current.toDocument(next)));
            runSearch(next);
        },
        [clearDebounceTimer, persist, runSearch, setApplied]
    );

    const setPage = useCallback(
        (page: number) => {
            flushPendingFilterPersist();
            const next = {...appliedRef.current, page};
            setApplied(next);
            runSearch(next);
        },
        [flushPendingFilterPersist, runSearch, setApplied]
    );

    const setFilters = useCallback(
        (partial: Partial<TFilter>) => {
            const next = {
                ...appliedRef.current,
                filters: {...appliedRef.current.filters, ...partial},
                page: 0
            };
            setApplied(next);
            pendingFilterPersistRef.current = true;
            scheduleDebouncedAction();
        },
        [scheduleDebouncedAction, setApplied]
    );

    // Unmount: abandons the shared timer just like every immediate mutator, and — same rule — persists
    // once more if a filter change is still pending instead of letting it disappear silently. Fire and
    // forget: `persist()` depends on no React state, so it completes fine after the component is gone.
    useEffect(
        () => () => {
            clearDebounceTimer();
            if (pendingFilterPersistRef.current) {
                pendingFilterPersistRef.current = false;
                persist(() => adapterRef.current.saveDocument(adapterRef.current.toDocument(appliedRef.current)));
            }
        },
        [clearDebounceTimer, persist]
    );

    // Referentially stable across renders as long as `adapter` itself is stable (see the adapter's own
    // useMemo in useOrderOverviewState/useRequestOverviewState) — `adapter.defaults()` otherwise builds a
    // fresh object every call, which would defeat memoization in Sheet/Badge components consuming these.
    const defaults = useMemo(() => adapter.defaults(), [adapter]);
    const defaultSize = useMemo(() => adapter.defaultSize(), [adapter]);

    // Memoized so the returned object keeps its identity across renders that change none of the fields
    // below — every mutator above is already a stable `useCallback`, so the only real dependencies are
    // the state slices themselves. Without this, callers (`OrderTrackingTable`'s `handleSortChange`, the
    // view pages' `orderTableNode`) would see a fresh object on every render and their own memoization
    // would never hit cache.
    return useMemo(
        () => ({
            applied,
            rows,
            totalElements,
            totalPages,
            totalUnreadMessages,
            defaults,
            defaultSize,
            resetAll,
            setSearchText,
            setSort,
            setSize,
            setPage,
            setFilters
        }),
        [
            applied,
            rows,
            totalElements,
            totalPages,
            totalUnreadMessages,
            defaults,
            defaultSize,
            resetAll,
            setSearchText,
            setSort,
            setSize,
            setPage,
            setFilters
        ]
    );
}
